import React, { useState, useEffect, useRef } from "react";
import {
  FaUser,
  FaHeart,
  FaBriefcase,
  FaGraduationCap,
  FaPrayingHands,
  FaMapMarkerAlt,
  FaUsers,
  FaImages,
  FaCamera,
  FaStar,
  FaBirthdayCake,
  FaWineGlassAlt,
  FaRulerVertical,
  FaHome,
  FaGlobeAsia,
  FaUserFriends,
  FaCertificate,
  FaCheckCircle,
  FaCheck,
  FaUserCheck,
  FaInfoCircle,
  FaEdit,
  FaSave,
  FaTimes,
  FaUpload,
  FaEnvelope,
  FaPhone,
  FaWhatsapp,
} from "react-icons/fa";
import CustomDropdown from "../../pages/admin/CustomDropdown";
import api from "../../services/api";

const Profile = ({ onProfileUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [masterData, setMasterData] = useState({
    education: [],
    occupations: [],
    religions: [],
    castes: {},
    subcastes: {},
  });
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState("");
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [viewImage, setViewImage] = useState(null);



  // Editing states
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});

  // Upload states
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [jathagamPhotoFile, setJathagamPhotoFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);

  // Preview states
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [jathagamPhotoPreview, setJathagamPhotoPreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  const mainContentRef = useRef(null);
  const BASE_URL = api.defaults.baseURL;
  const baseUrl = BASE_URL.replace("/api", "");

  useEffect(() => {
    fetchProfile();
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      const response = await api.get("/master-data");
      if (response.data && response.data.data) {
        setMasterData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching master data:", error);
    }
  };

  useEffect(() => {
    if (saveMessage || saveError) {
      const timer = setTimeout(() => {
        setSaveMessage("");
        setSaveError("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveMessage, saveError]);

  // Cleanup object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (profilePhotoPreview) URL.revokeObjectURL(profilePhotoPreview);
      if (jathagamPhotoPreview) URL.revokeObjectURL(jathagamPhotoPreview);
      galleryPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [profilePhotoPreview, jathagamPhotoPreview, galleryPreviews]);

  const fetchProfile = async () => {
    try {
      const response = await api.get("/dashboard/my-profile");
      setUser(response.data.user);
      const profileData = {
        isVerified: response.data.user.isVerified || false,
        completion: response.data.profile.completionPercentage || 0,
        photos: response.data.profile.photos || [],
        profilePhoto: response.data.profile.profilePhoto || null,
        jathagamPhoto: response.data.profile.jathagamPhoto || null,
        basicInfo: {
          fullName: `${response.data.profile.basicInfo?.firstName || ""} ${response.data.profile.basicInfo?.lastName || ""
            }`.trim(),
          gender: response.data.profile.basicInfo?.gender || "",
          dob: response.data.profile.basicInfo?.dob
            ? new Date(response.data.profile.basicInfo.dob).toLocaleDateString(
              "en-IN",
              { day: "numeric", month: "long", year: "numeric" }
            )
            : "",
          age: response.data.profile.basicInfo?.dob
            ? calculateAge(new Date(response.data.profile.basicInfo.dob))
            : "",
          maritalStatus: response.data.profile.basicInfo?.maritalStatus || "",
          height: response.data.profile.basicInfo?.heightFeet
            ? `${response.data.profile.basicInfo.heightFeet}${response.data.profile.basicInfo.heightInches
              ? `'${response.data.profile.basicInfo.heightInches}"`
              : ""
            }`
            : "",
          weight: response.data.profile.basicInfo?.weight || "",
          bodyType: response.data.profile.basicInfo?.bodyType || "",
          complexion: response.data.profile.basicInfo?.complexion || "",
          motherTongue: response.data.profile.basicInfo?.motherTongue || "",
          languagesKnown: response.data.profile.basicInfo?.languagesKnown || "",
          aboutMe: response.data.profile.basicInfo?.aboutMe || "",
        },
        contactInfo: {
          email: response.data.user?.email || "",
          mobile: response.data.user?.mobile || "",
          whatsappNumber: response.data.profile.contact?.whatsappNumber || "",
          alternateMobile: response.data.profile.contact?.alternateMobile || "",
          showMobile: response.data.profile.privacy?.showMobile || false,
          showEmail: response.data.profile.privacy?.showEmail || false,
        },
        educationCareer: {
          education: response.data.profile.educationCareer?.education || "",
          specialization:
            response.data.profile.educationCareer?.educationDetails || "",
          university: response.data.profile.educationCareer?.university || "",
          occupation: response.data.profile.educationCareer?.occupation || "",
          employedIn: response.data.profile.educationCareer?.employedIn || "",
          income: response.data.profile.educationCareer?.income || "",
          company: response.data.profile.educationCareer?.company || "",
          designation: response.data.profile.educationCareer?.designation || "",
          workLocation:
            response.data.profile.educationCareer?.workLocation || "",
        },
        religionCommunity: {
          religion: response.data.profile.religionCommunity?.religion || "",
          caste: response.data.profile.religionCommunity?.caste || "",
          subCaste: response.data.profile.religionCommunity?.subcaste || "",
          gothram: response.data.profile.religionCommunity?.gothram || "",
          familyOrigin: response.data.profile.location?.familyOrigin || "",
          familyValues: response.data.profile.family?.familyValues || "",
        },
        horoscope: {
          star: response.data.profile.horoscope?.star || "",
          pada: response.data.profile.horoscope?.pada || "",
          rasi: response.data.profile.horoscope?.rasi || "",
          nadi: response.data.profile.horoscope?.nadi || "",
          manglik: response.data.profile.horoscope?.manglik || "",
          dosham: response.data.profile.horoscope?.dosham || "",
          birthTime: response.data.profile.horoscope?.birthTime || "",
          birthPlace: response.data.profile.horoscope?.birthPlace || "",
        },
        family: {
          fatherStatus: response.data.profile.family?.fatherStatus || "",
          fatherOccupation:
            response.data.profile.family?.fatherOccupation || "",
          motherStatus: response.data.profile.family?.motherStatus || "",
          motherOccupation:
            response.data.profile.family?.motherOccupation || "",
          noOfBrothers: response.data.profile.family?.noOfBrothers ?? "",
          noOfSisters: response.data.profile.family?.noOfSisters ?? "",
          marriedBrothers: response.data.profile.family?.marriedBrothers || "",
          marriedSisters: response.data.profile.family?.marriedSisters || "",
          familyStatus: response.data.profile.family?.familyStatus || "",
          familyType: response.data.profile.family?.familyType || "",
          familyIncome: response.data.profile.family?.familyIncome || "",
          ancestralProperty:
            response.data.profile.family?.ancestralProperty || "",
          familyValues: response.data.profile.family?.familyValues || "",
          familyOrigin: response.data.profile.family?.familyOrigin || "",
        },
        location: {
          currentCity: response.data.profile.location?.currentCity || "",
          state: response.data.profile.location?.state || "",
          country: response.data.profile.location?.country || "",
          pincode: response.data.profile.location?.pincode || "",
          citizenship: response.data.profile.location?.citizenship || "",
          residencyStatus:
            response.data.profile.location?.residencyStatus || "",
          nativePlace: response.data.profile.location?.nativePlace || "",
        },
        lifestyle: {
          diet: response.data.profile.lifestyle?.diet || "",
          smoking: response.data.profile.lifestyle?.smoking || "",
          drinking: response.data.profile.lifestyle?.drinking || "",
          hobbies: response.data.profile.lifestyle?.hobbies || "",
        },
        partnerPreferences: {
          ageFrom: response.data.profile.partnerPreferences?.ageFrom || "",
          ageTo: response.data.profile.partnerPreferences?.ageTo || "",
          heightFrom:
            response.data.profile.partnerPreferences?.heightFrom || "",
          heightTo:
            response.data.profile.partnerPreferences?.heightTo || "",
          maritalStatus:
            response.data.profile.partnerPreferences?.maritalStatus || "",
          religion: response.data.profile.partnerPreferences?.religion || "",
          caste: response.data.profile.partnerPreferences?.caste || "",
          education: response.data.profile.partnerPreferences?.education || "",
          occupation:
            response.data.profile.partnerPreferences?.occupation || "",
          income: response.data.profile.partnerPreferences?.income || "",
          location: response.data.profile.partnerPreferences?.location || "",
          diet: response.data.profile.partnerPreferences?.diet || "",
          description:
            response.data.profile.partnerPreferences?.description || "",
        },
        expectations: response.data.profile.expectations || {},
        documents: {
          idProof: response.data.profile.documents?.idProof || null,
          addressProof: response.data.profile.documents?.addressProof || null,
          educationProof:
            response.data.profile.documents?.educationProof || null,
          incomeProof: response.data.profile.documents?.incomeProof || null,
        },
      };
      setProfile(profileData);
      setEditedData(profileData);
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dob) => {
    const diff = Date.now() - dob.getTime();
    const ageDate = new Date(diff);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };

  const startEditing = () => {
    setEditedData({ ...profile });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedData(profile);
  };

  const saveChanges = async () => {
    try {
      const dataToSend = {
        basicInfo: {
          fullName: editedData.basicInfo?.fullName,
          heightFeet: editedData.basicInfo?.height?.split("'")[0] || "",
          heightInches:
            editedData.basicInfo?.height?.includes("'")
              ? editedData.basicInfo.height.split("'")[1]?.replace('"', "")
              : "",
          weight: editedData.basicInfo?.weight,
          languagesKnown: editedData.basicInfo?.languagesKnown,
          aboutMe: editedData.basicInfo?.aboutMe,
        },
        educationCareer: {
          education: editedData.educationCareer?.education,
          specialization: editedData.educationCareer?.specialization,
          college: editedData.educationCareer?.college,
          university: editedData.educationCareer?.university,
          occupation: editedData.educationCareer?.occupation,
          employedIn: editedData.educationCareer?.employedIn,
          company: editedData.educationCareer?.company,
          designation: editedData.educationCareer?.designation,
          income: editedData.educationCareer?.income,
          workLocation: editedData.educationCareer?.workLocation,
        },
        lifestyle: {
          diet: editedData.lifestyle?.diet,
          smoking: editedData.lifestyle?.smoking,
          drinking: editedData.lifestyle?.drinking,
          hobbies: editedData.lifestyle?.hobbies,
        },
        partnerPreferences: editedData.partnerPreferences,
        expectations: editedData.expectations,
        location: {
          currentCity: editedData.location?.currentCity,
          state: editedData.location?.state,
          country: editedData.location?.country,
          pincode: editedData.location?.pincode,
          citizenship: editedData.location?.citizenship,
          residencyStatus: editedData.location?.residencyStatus,
          nativePlace: editedData.location?.nativePlace,
        },
        family: {
          fatherStatus: editedData.family?.fatherStatus,
          fatherOccupation: editedData.family?.fatherOccupation,
          motherStatus: editedData.family?.motherStatus,
          motherOccupation: editedData.family?.motherOccupation,
          noOfBrothers:
            editedData.family?.noOfBrothers || editedData.family?.brothers,
          marriedBrothers: editedData.family?.marriedBrothers,
          noOfSisters:
            editedData.family?.noOfSisters || editedData.family?.sisters,
          marriedSisters: editedData.family?.marriedSisters,
          familyType: editedData.family?.familyType,
          familyStatus: editedData.family?.familyStatus,
          familyIncome: editedData.family?.familyIncome,
          ancestralProperty: editedData.family?.ancestralProperty,
        },
      };
      await api.put("/profile", dataToSend);
      setProfile(editedData);
      setIsEditing(false);
      if (onProfileUpdate) onProfileUpdate();
      setSaveMessage("Profile updated successfully!");
      setSaveError("");
    } catch (error) {
      console.error("Error saving profile:", error);
      setSaveError("Failed to save changes. Please try again.");
    }
  };

  // Photo handlers with preview
  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhotoFile(file);
      setProfilePhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleJathagamPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setJathagamPhotoFile(file);
      setJathagamPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    setGalleryFiles(files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setGalleryPreviews(previews);
  };

  const handleProfilePhotoUpload = async () => {
    if (!profilePhotoFile) return;
    try {
      const formData = new FormData();
      formData.append("file", profilePhotoFile);
      const response = await api.post("/upload-profile-photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((prev) => ({ ...prev, profilePhoto: response.data.url }));
      setProfilePhotoFile(null);
      setProfilePhotoPreview(null);
      fetchProfile();
    } catch (error) {
      console.error("Error uploading profile photo:", error);
    }
  };

  const handleJathagamPhotoUpload = async () => {
    if (!jathagamPhotoFile) return;
    try {
      const formData = new FormData();
      formData.append("file", jathagamPhotoFile);
      const response = await api.post("/upload-jathagam-photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((prev) => ({ ...prev, jathagamPhoto: response.data.url }));
      setJathagamPhotoFile(null);
      setJathagamPhotoPreview(null);
      fetchProfile();
    } catch (error) {
      console.error("Error uploading jathagam photo:", error);
    }
  };

  const handleGalleryUpload = async () => {
    if (galleryFiles.length === 0) return;
    try {
      const formData = new FormData();
      galleryFiles.forEach((file) => formData.append("files", file));
      const response = await api.post("/upload-gallery-photos", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((prev) => ({
        ...prev,
        photos: [...prev.photos, ...response.data.urls],
      }));
      setGalleryFiles([]);
      setGalleryPreviews([]);
      document.getElementById("galleryUpload").value = "";
      fetchProfile();
    } catch (error) {
      console.error("Error uploading gallery photos:", error);
    }
  };
  const handleDeleteGalleryPhoto = async () => {
    try {
      await api.delete("/delete-gallery-photo", {
        data: { index: confirmDeleteIndex },
      });

      setProfile(prev => ({
        ...prev,
        photos: prev.photos.filter((_, i) => i !== confirmDeleteIndex),
      }));

      setIsConfirmOpen(false);
      setConfirmDeleteIndex(null);
    } catch (error) {
      console.error("Error deleting photo:", error);
      alert("Failed to delete photo");
    }
  };



  const editableSections = [
    "personal",
    "family",
    "educationCareer",
    "lifestyle",
    "partnerPreferences",
    "expectations",
    "location",
  ];

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    if (mainContentRef.current) {
      mainContentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
          <p className="mt-4 text-text-secondary">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile || !user) {
    return (
      <div className="text-center py-20">
        <p className="text-text-secondary">Profile data not available</p>
      </div>
    );
  }

  const sections = [
    { id: "overview", label: "Profile Overview", icon: <FaUserCheck /> },
    { id: "personal", label: "Personal Details", icon: <FaUser /> },
    { id: "family", label: "Family Details", icon: <FaUsers /> },
    {
      id: "educationCareer",
      label: "Education & Career",
      icon: <FaGraduationCap />,
    },
    {
      id: "religionCommunity",
      label: "Religion & Community",
      icon: <FaPrayingHands />,
    },
    { id: "horoscope", label: "Horoscope Details", icon: <FaStar /> },
    { id: "lifestyle", label: "Lifestyle", icon: <FaHeart /> },
    { id: "expectations", label: "Expectations", icon: <FaCheckCircle /> },
    { id: "location", label: "Location", icon: <FaMapMarkerAlt /> },
    { id: "photos", label: "Photos", icon: <FaImages /> },
  ];

  const isEditable = editableSections.includes(activeSection);

  return (
    <div className="min-h-screen bg-transparent">
      {/* Header */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="bg-white/80 dark:bg-[rgba(255,255,255,0.03)] backdrop-blur-[20px] border border-gray-100 dark:border-glass-border shadow-sm dark:shadow-glass-sm rounded-[24px] px-6 sm:px-8">
          <div className="flex items-center justify-between h-24">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white drop-shadow-sm">My Profile</h1>
              <p className="text-sm text-gray-600 dark:text-text-secondary mt-1">
                Complete matrimonial profile details
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-xs font-semibold text-gray-500 dark:text-text-muted uppercase tracking-wider">Profile ID</p>
                <p className="font-bold text-gray-900 dark:text-white tracking-wide mt-0.5">
                  {user._id?.slice(-8).toUpperCase()}
                </p>
              </div>
              {profile.isVerified && (
                <div className="bg-success-green/10 border border-success-green/20 px-3 py-1.5 rounded-full flex items-center ml-4">
                  <FaCheckCircle className="text-success-green mr-1.5" size={14} />
                  <span className="text-xs font-bold text-success-green tracking-wide uppercase">
                    Verified
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary rounded-lg shadow-sm border border-glass-border p-6 sticky top-8">
              <div className="text-center mb-6">
                <div className="relative inline-block">
                  <img
                    src={
                      profile.profilePhoto &&
                        profile.profilePhoto.trim().length > 0
                        ? profile.profilePhoto
                        : profile.basicInfo?.gender === "Female"
                          ? "https://randomuser.me/api/portraits/women/85.jpg"
                          : "https://randomuser.me/api/portraits/men/85.jpg"
                    }
                    alt="Profile"
                    className="w-32 h-32 rounded-full object-cover border-4 border-dark-navy shadow-[0_0_15px_rgba(255,255,255,0.1)] shadow-lg mx-auto"
                  />
                  {profile.isVerified && (
                    <div className="absolute bottom-2 right-2 bg-[rgba(59,130,246,0.1)]0 text-white p-1 rounded-full">
                      <FaCheck size={12} />
                    </div>
                  )}
                </div>
                <h2 className="text-xl font-bold text-text-primary drop-shadow-sm mt-4">
                  {profile.basicInfo.fullName}
                </h2>
                <p className="text-text-secondary">
                  {profile.basicInfo.age} years • {profile.basicInfo.maritalStatus}
                </p>
              </div>

              <div className="mb-8 pt-6 border-t border-glass-border">
                <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">
                  Profile Stats
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
                    <div className="text-xl font-bold text-text-primary drop-shadow-sm">
                      {profile.photos.length}
                    </div>
                    <div className="text-xs text-text-secondary">Photos</div>
                  </div>
                  <div className="text-center p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
                    <div className="text-xl font-bold text-text-primary drop-shadow-sm">
                      {profile.completion}%
                    </div>
                    <div className="text-xs text-text-secondary">Profile Score</div>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-text-secondary">
                    Profile Completion
                  </span>
                  <span className="text-sm font-bold text-primary-pink">
                    {profile.completion}%
                  </span>
                </div>
                <div className="w-full bg-[rgba(255,255,255,0.1)] rounded-full h-2">
                  <div
                    className="bg-primary-pink shadow-[0_0_15px_rgba(255,46,136,0.4)] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${profile.completion}%` }}
                  ></div>
                </div>
              </div>

              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">
                  Profile Sections
                </h3>
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionClick(section.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center transition-colors overflow-hidden ${activeSection === section.id
                      ? "bg-[rgba(255,46,136,0.1)] text-primary-pink drop-shadow-[0_0_5px_rgba(255,46,136,0.5)] border-l-4 border-primary-pink"
                      : "text-text-secondary hover:bg-[rgba(255,255,255,0.03)] hover:text-text-primary drop-shadow-sm border-l-4 border-transparent"
                      }`}
                  >
                    <span
                      className={`mr-3 flex-shrink-0 ${activeSection === section.id
                        ? "text-primary-pink"
                        : "text-text-muted"
                        }`}
                    >
                      {section.icon}
                    </span>
                    <span className="font-medium text-sm truncate">{section.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3" ref={mainContentRef}>
            <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary rounded-lg shadow-sm border border-glass-border">
              <div className="border-b border-glass-border px-6 py-4 flex justify-between items-center">
                <div className="flex items-center">
                  <div className="p-2 bg-[rgba(255,46,136,0.1)] rounded-lg mr-3">
                    {sections.find((s) => s.id === activeSection)?.icon}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-text-primary drop-shadow-sm">
                      {sections.find((s) => s.id === activeSection)?.label}
                    </h2>
                    <p className="text-sm text-text-secondary">
                      Complete matrimonial information
                    </p>
                  </div>
                </div>
                {isEditable && !isEditing && (
                  <button
                    onClick={startEditing}
                    className="flex items-center px-4 py-2 bg-primary-pink shadow-[0_0_15px_rgba(255,46,136,0.4)] text-white rounded-lg hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)] transition"
                  >
                    <FaEdit className="mr-2" />
                    Edit
                  </button>
                )}
                {isEditing && (
                  <div className="flex space-x-2">
                    <button
                      onClick={saveChanges}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                      <FaSave className="mr-2" />
                      Save
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="flex items-center px-4 py-2 bg-[rgba(255,255,255,0.03)]0 text-white rounded-lg hover:bg-gray-600 transition"
                    >
                      <FaTimes className="mr-2" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {saveMessage && (
                <div className="mx-6 mt-4 text-sm text-green-600 font-medium">
                  {saveMessage}
                </div>
              )}
              {saveError && (
                <div className="mx-6 mt-4 text-sm text-red-600 font-medium">
                  {saveError}
                </div>
              )}

              <div className="p-6">
                {activeSection === "overview" && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                      <InfoCard
                        title="Basic Information"
                        icon={<FaUser className="text-primary-pink" />}
                        items={[
                          { label: "Age", value: `${profile.basicInfo.age} years` },
                          { label: "Gender", value: profile.basicInfo.gender },
                          { label: "Marital Status", value: profile.basicInfo.maritalStatus },
                          { label: "Height", value: profile.basicInfo.height },
                        ]}
                      />
                      <InfoCard
                        title="Education & Career"
                        icon={<FaGraduationCap className="text-blue-400" />}
                        items={[
                          { label: "Education", value: profile.educationCareer.education },
                          { label: "Occupation", value: profile.educationCareer.occupation },
                          { label: "Company", value: profile.educationCareer.company },
                          { label: "Income", value: profile.educationCareer.income },
                        ]}
                      />
                      <InfoCard
                        title="Religion & Community"
                        icon={<FaPrayingHands className="text-purple-600" />}
                        items={[
                          { label: "Religion", value: profile.religionCommunity.religion },
                          { label: "Caste", value: profile.religionCommunity.caste },
                          { label: "Star", value: profile.horoscope.star },
                          { label: "Rasi", value: profile.horoscope.rasi },
                        ]}
                      />
                    </div>

                    <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-5">
                      <h3 className="font-bold text-text-primary drop-shadow-sm mb-3 flex items-center">
                        <FaPhone className="mr-2 text-text-muted" />
                        Contact Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary p-4 rounded border border-glass-border">
                          <div className="flex items-center mb-2">
                            <FaEnvelope className="text-text-muted mr-2" />
                            <span className="text-sm font-medium text-text-secondary">Email</span>
                          </div>
                          <div className="text-text-primary drop-shadow-sm font-medium">{profile.contactInfo.email}</div>
                        </div>
                        <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary p-4 rounded border border-glass-border">
                          <div className="flex items-center mb-2">
                            <FaPhone className="text-text-muted mr-2" />
                            <span className="text-sm font-medium text-text-secondary">Mobile</span>
                          </div>
                          <div className="text-text-primary drop-shadow-sm font-medium">{profile.contactInfo.mobile}</div>
                        </div>
                        {profile.contactInfo.whatsappNumber && (
                          <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary p-4 rounded border border-glass-border">
                            <div className="flex items-center mb-2">
                              <FaWhatsapp className="text-green-500 mr-2" />
                              <span className="text-sm font-medium text-text-secondary">WhatsApp</span>
                            </div>
                            <div className="text-text-primary drop-shadow-sm font-medium">{profile.contactInfo.whatsappNumber}</div>
                          </div>
                        )}
                        {profile.contactInfo.alternateMobile && (
                          <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary p-4 rounded border border-glass-border">
                            <div className="flex items-center mb-2">
                              <FaPhone className="text-text-muted mr-2" />
                              <span className="text-sm font-medium text-text-secondary">Alternate Mobile</span>
                            </div>
                            <div className="text-text-primary drop-shadow-sm font-medium">{profile.contactInfo.alternateMobile}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === "personal" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SectionCard
                      title="Personal Information"
                      icon={<FaUser className="text-primary-pink" />}
                      items={[
                        {
                          label: "Full Name", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.basicInfo?.fullName || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                basicInfo: { ...editedData.basicInfo, fullName: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.basicInfo.fullName
                        },
                        { label: "Gender", value: profile.basicInfo.gender },
                        { label: "Date of Birth", value: profile.basicInfo.dob },
                        { label: "Age", value: `${profile.basicInfo.age} years` },
                        { label: "Marital Status", value: profile.basicInfo.maritalStatus },
                      ]}
                    />
                    <SectionCard
                      title="Physical Attributes"
                      icon={<FaRulerVertical className="text-blue-400" />}
                      items={[
                        {
                          label: "Height", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.basicInfo?.height || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                basicInfo: { ...editedData.basicInfo, height: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.basicInfo.height
                        },
                        {
                          label: "Weight", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.basicInfo?.weight || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                basicInfo: { ...editedData.basicInfo, weight: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.basicInfo.weight
                        },
                        { label: "Body Type", value: profile.basicInfo.bodyType },
                        { label: "Complexion", value: profile.basicInfo.complexion },
                      ]}
                    />
                    <SectionCard
                      title="Contact Information"
                      icon={<FaPhone className="text-green-600" />}
                      className="md:col-span-2"
                      items={[
                        { label: "Email", value: profile.contactInfo.email },
                        { label: "Mobile", value: profile.contactInfo.mobile },
                        { label: "WhatsApp", value: profile.contactInfo.whatsappNumber || "Not specified" },
                        { label: "Alternate Mobile", value: profile.contactInfo.alternateMobile || "Not specified" },
                        { label: "Show Mobile to Matches", value: profile.contactInfo.showMobile ? "Yes" : "No" },
                        { label: "Show Email to Matches", value: profile.contactInfo.showEmail ? "Yes" : "No" },
                      ]}
                    />
                  </div>
                )}

                {activeSection === "family" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SectionCard
                      title="Parents Information"
                      icon={<FaUsers className="text-primary-pink" />}
                      items={[
                        {
                          label: "Father's Status", value: isEditing ? (
                            <CustomDropdown
                              options={[
                                { value: "", label: "Select Status" },
                                { value: "Alive", label: "Alive" },
                                { value: "Deceased", label: "Deceased" },
                                { value: "Retired", label: "Retired" },
                                { value: "Working", label: "Working" },
                              ]}
                              value={editedData.family?.fatherStatus || ""}
                              onChange={(v) => setEditedData({
                                ...editedData,
                                family: { ...editedData.family, fatherStatus: v },
                              })}
                              placeholder="Select Status"
                            />
                          ) : profile.family.fatherStatus || "Not specified"
                        },
                        {
                          label: "Father's Occupation", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.family?.fatherOccupation || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                family: { ...editedData.family, fatherOccupation: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.family.fatherOccupation || "Not specified"
                        },
                        {
                          label: "Mother's Status", value: isEditing ? (
                            <CustomDropdown
                              options={[
                                { value: "", label: "Select Status" },
                                { value: "Housewife", label: "Housewife" },
                                { value: "Working", label: "Working" },
                                { value: "Retired", label: "Retired" },
                                { value: "Deceased", label: "Deceased" },
                              ]}
                              value={editedData.family?.motherStatus || ""}
                              onChange={(v) => setEditedData({
                                ...editedData,
                                family: { ...editedData.family, motherStatus: v },
                              })}
                              placeholder="Select Status"
                            />
                          ) : profile.family.motherStatus || "Not specified"
                        },
                        {
                          label: "Mother's Occupation", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.family?.motherOccupation || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                family: { ...editedData.family, motherOccupation: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.family.motherOccupation || "Not specified"
                        },
                      ]}
                    />
                    <SectionCard
                      title="Siblings Details"
                      icon={<FaUserFriends className="text-blue-400" />}
                      items={[
                        {
                          label: "Number of Brothers", value: isEditing ? (
                            <input
                              type="number"
                              min="0"
                              value={editedData.family?.noOfBrothers || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                family: { ...editedData.family, noOfBrothers: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.family.noOfBrothers || "Not specified"
                        },
                        {
                          label: "Number of Sisters", value: isEditing ? (
                            <input
                              type="number"
                              min="0"
                              value={editedData.family?.noOfSisters || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                family: { ...editedData.family, noOfSisters: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.family.noOfSisters || "Not specified"
                        },
                      ]}
                    />
                    <SectionCard
                      title="Family Background"
                      icon={<FaHome className="text-green-600" />}
                      className="md:col-span-2"
                      items={[
                        {
                          label: "Family Type", value: isEditing ? (
                            <CustomDropdown
                              options={[
                                { value: "", label: "Select Type" },
                                { value: "Joint Family", label: "Joint Family" },
                                { value: "Nuclear Family", label: "Nuclear Family" },
                                { value: "Extended Family", label: "Extended Family" },
                              ]}
                              value={editedData.family?.familyType || ""}
                              onChange={(v) => setEditedData({
                                ...editedData,
                                family: { ...editedData.family, familyType: v },
                              })}
                              placeholder="Select Type"
                            />
                          ) : profile.family.familyType || "Not specified"
                        },
                      ]}
                    />
                  </div>
                )}

                {activeSection === "educationCareer" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SectionCard
                      title="Education Details"
                      icon={<FaGraduationCap className="text-primary-pink" />}
                      items={[
                        {
                          label: "Highest Education", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.educationCareer?.education || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                educationCareer: { ...editedData.educationCareer, education: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.educationCareer.education
                        },
                        {
                          label: "Specialization", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.educationCareer?.specialization || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                educationCareer: { ...editedData.educationCareer, specialization: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.educationCareer.specialization
                        },
                        {
                          label: "College/University", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.educationCareer?.university || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                educationCareer: { ...editedData.educationCareer, university: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.educationCareer.university
                        },
                      ]}
                    />
                    <SectionCard
                      title="Career Details"
                      icon={<FaBriefcase className="text-blue-400" />}
                      items={[
                        {
                          label: "Occupation", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.educationCareer?.occupation || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                educationCareer: { ...editedData.educationCareer, occupation: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.educationCareer.occupation
                        },
                        {
                          label: "Company", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.educationCareer?.company || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                educationCareer: { ...editedData.educationCareer, company: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.educationCareer.company
                        },
                        {
                          label: "Designation", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.educationCareer?.designation || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                educationCareer: { ...editedData.educationCareer, designation: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.educationCareer.designation
                        },
                        {
                          label: "Annual Income", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.educationCareer?.income || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                educationCareer: { ...editedData.educationCareer, income: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.educationCareer.income
                        },
                      ]}
                    />
                  </div>
                )}

                {activeSection === "religionCommunity" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SectionCard
                      title="Religion & Community"
                      icon={<FaPrayingHands className="text-primary-pink" />}
                      items={[
                        { label: "Religion", value: profile.religionCommunity.religion },
                        { label: "Caste", value: profile.religionCommunity.caste },
                        { label: "Sub-caste", value: profile.religionCommunity.subCaste },
                        { label: "Gothram", value: profile.religionCommunity.gothram },
                        { label: "Family Origin", value: profile.religionCommunity.familyOrigin },
                      ]}
                    />
                    <SectionCard
                      title="Family Values"
                      icon={<FaHome className="text-blue-400" />}
                      items={[
                        { label: "Family Values", value: profile.religionCommunity.familyValues },
                      ]}
                    />
                  </div>
                )}

                {activeSection === "horoscope" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SectionCard
                      title="Astrological Details"
                      icon={<FaStar className="text-primary-pink" />}
                      items={[
                        { label: "Star (Nakshatra)", value: profile.horoscope.star },
                        { label: "Pada", value: profile.horoscope.pada },
                        { label: "Rasi (Moon Sign)", value: profile.horoscope.rasi },
                        { label: "Nadi", value: profile.horoscope.nadi },
                        { label: "Manglik", value: profile.horoscope.manglik },
                        { label: "Dosham", value: profile.horoscope.dosham },
                      ]}
                    />
                    <SectionCard
                      title="Birth Details"
                      icon={<FaBirthdayCake className="text-blue-400" />}
                      items={[
                        {
                          label: "Birth Time", value: isEditing ? (
                            <div className="flex gap-2">
                              <div className="w-1/3">
                                <CustomDropdown
                                  value={editedData.horoscope?.birthTime?.substring(0, 2).trim() || ''}
                                  onChange={(v) => {
                                    const h = v;
                                    const m = editedData.horoscope?.birthTime?.substring(3, 5).padEnd(2, ' ') || '  ';
                                    const a = editedData.horoscope?.birthTime?.substring(6, 8).padEnd(2, ' ') || '  ';
                                    setEditedData({
                                      ...editedData,
                                      horoscope: { ...editedData.horoscope, birthTime: `${h}:${m} ${a}` }
                                    });
                                  }}
                                  options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1).padStart(2, '0'), label: String(i + 1).padStart(2, '0') }))}
                                  placeholder="HH"
                                />
                              </div>
                              <div className="w-1/3">
                                <CustomDropdown
                                  value={editedData.horoscope?.birthTime?.substring(3, 5).trim() || ''}
                                  onChange={(v) => {
                                    const m = v;
                                    const h = editedData.horoscope?.birthTime?.substring(0, 2).padEnd(2, ' ') || '  ';
                                    const a = editedData.horoscope?.birthTime?.substring(6, 8).padEnd(2, ' ') || '  ';
                                    setEditedData({
                                      ...editedData,
                                      horoscope: { ...editedData.horoscope, birthTime: `${h}:${m} ${a}` }
                                    });
                                  }}
                                  options={Array.from({ length: 60 }, (_, i) => ({ value: String(i).padStart(2, '0'), label: String(i).padStart(2, '0') }))}
                                  placeholder="MM"
                                />
                              </div>
                              <div className="w-1/3">
                                <CustomDropdown
                                  value={editedData.horoscope?.birthTime?.substring(6, 8).trim() || ''}
                                  onChange={(v) => {
                                    const a = v;
                                    const h = editedData.horoscope?.birthTime?.substring(0, 2).padEnd(2, ' ') || '  ';
                                    const m = editedData.horoscope?.birthTime?.substring(3, 5).padEnd(2, ' ') || '  ';
                                    setEditedData({
                                      ...editedData,
                                      horoscope: { ...editedData.horoscope, birthTime: `${h}:${m} ${a}` }
                                    });
                                  }}
                                  options={[{ value: 'AM', label: 'AM' }, { value: 'PM', label: 'PM' }]}
                                  placeholder="AM/PM"
                                />
                              </div>
                            </div>
                          ) : profile.horoscope.birthTime
                        },
                        { label: "Birth Place", value: profile.horoscope.birthPlace },
                      ]}
                    />
                  </div>
                )}

                {activeSection === "lifestyle" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SectionCard
                      title="Diet & Habits"
                      icon={<FaHeart className="text-primary-pink" />}
                      items={[
                        {
                          label: "Diet", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.lifestyle?.diet || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                lifestyle: { ...editedData.lifestyle, diet: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.lifestyle.diet
                        },
                        {
                          label: "Smoking", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.lifestyle?.smoking || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                lifestyle: { ...editedData.lifestyle, smoking: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.lifestyle.smoking
                        },
                        {
                          label: "Drinking", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.lifestyle?.drinking || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                lifestyle: { ...editedData.lifestyle, drinking: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.lifestyle.drinking
                        },
                      ]}
                    />
                  </div>
                )}

                {activeSection === "expectations" && (
                  <div className="grid grid-cols-1 gap-6">
                    <SectionCard
                      title="Basic Expectations"
                      style={{ position: "relative", zIndex: 100 }}
                      icon={<FaCheckCircle className="text-primary-pink" />}
                      items={[
                        {
                          label: "Age Range", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.basicInfo?.ageRange || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, basicInfo: { ...editedData.expectations?.basicInfo, ageRange: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "18 - 25", label: "18 - 25" }, { value: "26 - 30", label: "26 - 30" }, { value: "31 - 35", label: "31 - 35" }, { value: "36 - 40", label: "36 - 40" }, { value: "41+", label: "41+" }]}
                            />
                          ) : profile.expectations?.basicInfo?.ageRange || "Any"
                        },
                        {
                          label: "Marital Status", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.basicInfo?.maritalStatus || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, basicInfo: { ...editedData.expectations?.basicInfo, maritalStatus: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Never Married", label: "Never Married" }, { value: "Divorced", label: "Divorced" }, { value: "Widowed", label: "Widowed" },{ value: "Separated", label: "Separated" }, { value: "Awaiting Divorce", label: "Awaiting Divorce" }]}
                            />
                          ) : profile.expectations?.basicInfo?.maritalStatus || "Any"
                        },
                        {
                          label: "Body Type", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.basicInfo?.bodyType || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, basicInfo: { ...editedData.expectations?.basicInfo, bodyType: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Slim", label: "Slim" }, { value: "Average", label: "Average" }, { value: "Athletic", label: "Athletic" }, { value: "Heavy", label: "Heavy" }]}
                            />
                          ) : profile.expectations?.basicInfo?.bodyType || "Any"
                        },
                        {
                          label: "Complexion", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.basicInfo?.complexion || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, basicInfo: { ...editedData.expectations?.basicInfo, complexion: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Very Fair", label: "Very Fair" }, { value: "Fair", label: "Fair" }, { value: "Wheatish", label: "Wheatish" }, { value: "Dark", label: "Dark" }]}
                            />
                          ) : profile.expectations?.basicInfo?.complexion || "Any"
                        },
                        /*{
                          label: "Physical Status", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.basicInfo?.physicalStatus || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, basicInfo: { ...editedData.expectations?.basicInfo, physicalStatus: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Normal", label: "Normal" }, { value: "Physically Challenged", label: "Physically Challenged" }]}
                            />
                          ) : profile.expectations?.basicInfo?.physicalStatus || "Any"
                        }*/
                      ]}
                    />

                    <SectionCard
                      title="Education & Career Expectations"
                      style={{ position: "relative", zIndex: 90 }}
                      icon={<FaBriefcase className="text-primary-pink" />}
                      items={[
                        {
                          label: "Highest Qualification", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.educationCareer?.highestQualification || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, educationCareer: { ...editedData.expectations?.educationCareer, highestQualification: val } },
                              })}
                              options={[{ value: "", label: "Any" }, ...(masterData.education || []).map((e) => ({ value: e, label: e }))]}
                            />
                          ) : profile.expectations?.educationCareer?.highestQualification || "Any"
                        },
                        {
                          label: "Occupation", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.educationCareer?.occupation || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, educationCareer: { ...editedData.expectations?.educationCareer, occupation: val } },
                              })}
                              options={[{ value: "", label: "Any" }, ...(masterData.occupations || []).map((o) => ({ value: o, label: o }))]}
                            />
                          ) : profile.expectations?.educationCareer?.occupation || "Any"
                        },
                        {
                          label: "Annual Income", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.educationCareer?.annualIncome || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, educationCareer: { ...editedData.expectations?.educationCareer, annualIncome: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "0-2 Lakhs", label: "Less than 2 Lakhs" }, { value: "2-5 Lakhs", label: "2 to 5 Lakhs" }, { value: "5-10 Lakhs", label: "5 to 10 Lakhs" }, { value: "10-20 Lakhs", label: "10 to 20 Lakhs" }, { value: "20+ Lakhs", label: "More than 20 Lakhs" }]}
                            />


   
                          ) : profile.expectations?.educationCareer?.annualIncome || "Any"
                        }
                      ]}
                    />

                    <SectionCard
                      title="Location Expectations"
                      style={{ position: "relative", zIndex: 80 }}
                      icon={<FaMapMarkerAlt className="text-primary-pink" />}
                      items={[
                        {
                          label: "Country", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.location?.country || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, location: { ...editedData.expectations?.location, country: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "India", label: "India" }, { value: "USA", label: "USA" }, { value: "UK", label: "UK" }, { value: "UAE", label: "UAE" }, { value: "Australia", label: "Australia" }]}
                            />
                          ) : profile.expectations?.location?.country || "Any"
                        },
                        {
                          label: "State", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.location?.state || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, location: { ...editedData.expectations?.location, state: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Tamil Nadu", label: "Tamil Nadu" }, { value: "Kerala", label: "Kerala" }, { value: "Karnataka", label: "Karnataka" }, { value: "Andhra Pradesh", label: "Andhra Pradesh" }, { value: "Maharashtra", label: "Maharashtra" }]}
                            />
                          ) : profile.expectations?.location?.state || "Any"
                        },
                        {
                          label: "District/City", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.expectations?.location?.districtCity || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, location: { ...editedData.expectations?.location, districtCity: e.target.value } },
                              })}
                              placeholder="Any"
                              className="w-full bg-glass-card border border-glass-border rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:border-primary-pink"
                            />
                          ) : profile.expectations?.location?.districtCity || "Any"
                        }
                      ]}
                    />

                    <SectionCard
                      title="Family Expectations"
                      style={{ position: "relative", zIndex: 70 }}
                      icon={<FaUsers className="text-primary-pink" />}
                      items={[
                        {
                          label: "Family Type", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.family?.familyType || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, family: { ...editedData.expectations?.family, familyType: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Nuclear", label: "Nuclear" }, { value: "Joint", label: "Joint" }, { value: "Extended", label: "Extended" }]}
                            />
                          ) : profile.expectations?.family?.familyType || "Any"
                        },
                        {
                          label: "Family Values", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.family?.familyValues || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, family: { ...editedData.expectations?.family, familyValues: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Traditional", label: "Traditional" }, { value: "Moderate", label: "Moderate" }, { value: "Liberal", label: "Liberal" }]}
                            />
                          ) : profile.expectations?.family?.familyValues || "Any"
                        }
                      ]}
                    />

                    <SectionCard
                      title="Religion & Community Expectations"
                      style={{ position: "relative", zIndex: 60 }}
                      icon={<FaPrayingHands className="text-primary-pink" />}
                      items={[
                        {
                          label: "Religion", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.religionCommunity?.religion || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, religionCommunity: { ...editedData.expectations?.religionCommunity, religion: val } },
                              })}
                              options={[{ value: "", label: "Any" }, ...(masterData.religions || []).map((r) => ({ value: r, label: r }))]}
                            />
                          ) : profile.expectations?.religionCommunity?.religion || "Any"
                        },
                        {
                          label: "Caste", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.expectations?.religionCommunity?.caste || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, religionCommunity: { ...editedData.expectations?.religionCommunity, caste: e.target.value } },
                              })}
                              placeholder="Any"
                              className="w-full bg-glass-card border border-glass-border rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:border-primary-pink"
                            />
                          ) : profile.expectations?.religionCommunity?.caste || "Any"
                        },
                        {
                          label: "Subcaste", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.expectations?.religionCommunity?.subcaste || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, religionCommunity: { ...editedData.expectations?.religionCommunity, subcaste: e.target.value } },
                              })}
                              placeholder="Any"
                              className="w-full bg-glass-card border border-glass-border rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:border-primary-pink"
                            />
                          ) : profile.expectations?.religionCommunity?.subcaste || "Any"
                        },
                        {
                          label: "Gothram", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.expectations?.religionCommunity?.gothram || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, religionCommunity: { ...editedData.expectations?.religionCommunity, gothram: e.target.value } },
                              })}
                              placeholder="Any"
                              className="w-full bg-glass-card border border-glass-border rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:border-primary-pink"
                            />
                          ) : profile.expectations?.religionCommunity?.gothram || "Any"
                        }
                      ]}
                    />

                    <SectionCard
                      title="Horoscope Expectations"
                      style={{ position: "relative", zIndex: 50 }}
                      icon={<FaStar className="text-primary-pink" />}
                      items={[
                        {
                          label: "Star (Nakshatra)", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.horoscope?.star || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, horoscope: { ...editedData.expectations?.horoscope, star: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Ashwini", label: "Ashwini" }, { value: "Bharani", label: "Bharani" }, { value: "Krittika", label: "Krittika" }, { value: "Rohini", label: "Rohini" }, { value: "Mrigashirsha", label: "Mrigashirsha" }, { value: "Ardra", label: "Ardra" }, { value: "Punarvasu", label: "Punarvasu" }, { value: "Pushya", label: "Pushya" }, { value: "Ashlesha", label: "Ashlesha" }, { value: "Magha", label: "Magha" }]}
                            />
                          ) : profile.expectations?.horoscope?.star || "Any"
                        },
                        {
                          label: "Rasi (Moon Sign)", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.horoscope?.rasi || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, horoscope: { ...editedData.expectations?.horoscope, rasi: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Mesham", label: "Mesham" }, { value: "Rishabam", label: "Rishabam" }, { value: "Midhunam", label: "Midhunam" }, { value: "Kadagam", label: "Kadagam" }, { value: "Simmam", label: "Simmam" }, { value: "Kanni", label: "Kanni" }]}
                            />
                          ) : profile.expectations?.horoscope?.rasi || "Any"
                        },
                        {
                          label: "Dosham", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.horoscope?.dosham || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, horoscope: { ...editedData.expectations?.horoscope, dosham: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "No", label: "No" }, { value: "Yes", label: "Yes" }, { value: "Don't Know", label: "Don't Know" }]}
                            />
                          ) : profile.expectations?.horoscope?.dosham || "Any"
                        },
                        {
                          label: "Manglik", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.horoscope?.manglik || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, horoscope: { ...editedData.expectations?.horoscope, manglik: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                            />
                          ) : profile.expectations?.horoscope?.manglik || "Any"
                        }
                      ]}
                    />

                    <SectionCard
                      title="Lifestyle Expectations"
                      style={{ position: "relative", zIndex: 40 }}
                      icon={<FaWineGlassAlt className="text-primary-pink" />}
                      items={[
                        {
                          label: "Diet", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.lifestyle?.diet || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, lifestyle: { ...editedData.expectations?.lifestyle, diet: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "Vegetarian", label: "Vegetarian" }, { value: "Non-Vegetarian", label: "Non-Vegetarian" }, { value: "Eggetarian", label: "Eggetarian" }, { value: "Vegan", label: "Vegan" }]}
                            />
                          ) : profile.expectations?.lifestyle?.diet || "Any"
                        },
                        {
                          label: "Smoking", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.lifestyle?.smoking || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, lifestyle: { ...editedData.expectations?.lifestyle, smoking: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "No", label: "No" }, { value: "Yes", label: "Yes" }, { value: "Occasionally", label: "Occasionally" }]}
                            />
                          ) : profile.expectations?.lifestyle?.smoking || "Any"
                        },
                        {
                          label: "Drinking", value: isEditing ? (
                            <CustomDropdown
                              value={editedData.expectations?.lifestyle?.drinking || ""}
                              onChange={(val) => setEditedData({
                                ...editedData,
                                expectations: { ...editedData.expectations, lifestyle: { ...editedData.expectations?.lifestyle, drinking: val } },
                              })}
                              options={[{ value: "", label: "Any" }, { value: "No", label: "No" }, { value: "Yes", label: "Yes" }, { value: "Occasionally", label: "Occasionally" }]}
                            />
                          ) : profile.expectations?.lifestyle?.drinking || "Any"
                        }
                      ]}
                    />
                  </div>
                )}

                {activeSection === "location" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SectionCard
                      title="Current Location"
                      icon={<FaMapMarkerAlt className="text-primary-pink" />}
                      items={[
                        {
                          label: "City", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.location?.currentCity || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                location: { ...editedData.location, currentCity: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.location.currentCity
                        },
                        {
                          label: "State", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.location?.state || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                location: { ...editedData.location, state: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.location.state
                        },
                        {
                          label: "Country", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.location?.country || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                location: { ...editedData.location, country: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.location.country
                        },
                        {
                          label: "Pincode", value: isEditing ? (
                            <input
                              type="text"
                              value={editedData.location?.pincode || ""}
                              onChange={(e) => setEditedData({
                                ...editedData,
                                location: { ...editedData.location, pincode: e.target.value },
                              })}
                              className="bg-[rgba(255,255,255,0.05)] text-text-primary border border-glass-border rounded px-3 py-2 w-full focus:ring-2 focus:ring-primary-pink outline-none placeholder:text-text-muted"
                            />
                          ) : profile.location.pincode
                        },
                      ]}
                    />
                  </div>
                )}

                {activeSection === "photos" && (
                  <div className="space-y-8">
                    {/* Profile Photo */}
                    <div className="bg-glass-card backdrop-blur-xl shadow-glass text-text-primary border border-glass-border rounded-lg p-6">
                      <h3 className="font-bold text-text-primary drop-shadow-sm mb-4 flex items-center">
                        <FaCamera className="mr-2 text-text-muted" />
                        Profile Photo
                      </h3>
                      <div className="flex justify-center">
                        <img
                          src={
                            profilePhotoPreview ||
                            (profile.profilePhoto && profile.profilePhoto.trim().length > 0
                              ? profile.profilePhoto
                              : profile.basicInfo?.gender === "Female"
                                ? "https://randomuser.me/api/portraits/women/85.jpg"
                                : "https://randomuser.me/api/portraits/men/85.jpg")
                          }
                          alt="Profile Preview"
                          className="w-48 h-48 rounded-full object-cover border-4 border-dark-navy shadow-[0_0_15px_rgba(255,255,255,0.1)] shadow"
                        />
                      </div>
                      <div className="mt-6 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePhotoChange}
                          className="hidden"
                          id="profilePhotoUpload"
                        />
                        <label
                          htmlFor="profilePhotoUpload"
                          className="cursor-pointer inline-flex items-center px-5 py-3 bg-primary-pink shadow-[0_0_15px_rgba(255,46,136,0.4)] text-white rounded-lg hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)] transition"
                        >
                          <FaUpload className="mr-2" />
                          {profilePhotoPreview ? "Change Profile Photo" : "Upload Profile Photo"}
                        </label>
                        {profilePhotoFile && (
                          <div className="mt-4">
                            <button
                              onClick={handleProfilePhotoUpload}
                              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                            >
                              Save Profile Photo
                            </button>
                            <button
                              onClick={() => {
                                setProfilePhotoFile(null);
                                setProfilePhotoPreview(null);
                                document.getElementById("profilePhotoUpload").value = "";
                              }}
                              className="ml-3 px-6 py-3 bg-[rgba(255,255,255,0.03)]0 text-white rounded-lg hover:bg-gray-600 transition"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Jathagam Photo */}
                    <div className="bg-glass-card backdrop-blur-xl shadow-glass text-text-primary border border-glass-border rounded-lg p-6">
                      <h3 className="font-bold text-text-primary drop-shadow-sm mb-4 flex items-center">
                        <FaCertificate className="mr-2 text-text-muted" />
                        Jathagam / Horoscope
                      </h3>
                      {(jathagamPhotoPreview || profile.jathagamPhoto) ? (
                        <div className="flex justify-center">
                          <img
                            src={jathagamPhotoPreview || profile.jathagamPhoto}
                            alt="Jathagam Preview"
                            className="max-w-full h-auto max-h-96 rounded border border-glass-border shadow"
                          />
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <FaCertificate className="text-6xl text-text-muted mx-auto mb-4" />
                          <p className="text-text-muted">No jathagam uploaded yet</p>
                        </div>
                      )}
                      <div className="mt-6 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleJathagamPhotoChange}
                          className="hidden"
                          id="jathagamPhotoUpload"
                        />
                        <label
                          htmlFor="jathagamPhotoUpload"
                          className="cursor-pointer inline-flex items-center px-5 py-3 bg-primary-pink shadow-[0_0_15px_rgba(255,46,136,0.4)] text-white rounded-lg hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)] transition"
                        >
                          <FaUpload className="mr-2" />
                          {jathagamPhotoPreview ? "Change Jathagam" : "Upload Jathagam Photo"}
                        </label>
                        {jathagamPhotoFile && (
                          <div className="mt-4">
                            <button
                              onClick={handleJathagamPhotoUpload}
                              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                            >
                              Save Jathagam
                            </button>
                            <button
                              onClick={() => {
                                setJathagamPhotoFile(null);
                                setJathagamPhotoPreview(null);
                                document.getElementById("jathagamPhotoUpload").value = "";
                              }}
                              className="ml-3 px-6 py-3 bg-[rgba(255,255,255,0.03)]0 text-white rounded-lg hover:bg-gray-600 transition"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Gallery Photos */}
                    <div className="bg-glass-card backdrop-blur-xl shadow-glass text-text-primary border border-glass-border rounded-lg p-6">
                      <h3 className="font-bold text-text-primary drop-shadow-sm mb-4 flex items-center">
                        <FaImages className="mr-2 text-text-muted" />
                        Photo Gallery ({profile.photos.length + galleryPreviews.length})
                      </h3>

                      {profile.photos.length > 0 && (
                        <div className="mb-8">
                          <h4 className="text-sm font-medium text-text-secondary mb-3">Current Photos</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {profile.photos.map((photo, index) => (
                              <div
                                key={index}
                                className="relative aspect-square overflow-hidden rounded border border-glass-border group"
                              >
                                <img
                                  src={
                                    photo.startsWith("http") ||
                                      photo.startsWith("blob:") ||
                                      photo.startsWith("data:")
                                      ? photo
                                      : `${baseUrl}${photo}`
                                  }
                                  alt={`Gallery ${index + 1}`}
                                  onClick={() =>
                                    setViewImage(
                                      photo.startsWith("http") ||
                                        photo.startsWith("blob:") ||
                                        photo.startsWith("data:")
                                        ? photo
                                        : `${baseUrl}${photo}`
                                    )
                                  }
                                  className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-300"
                                />


                                {/* DELETE BUTTON */}
                                <button
                                  onClick={() => {
                                    setConfirmDeleteIndex(index);
                                    setIsConfirmOpen(true);
                                  }}
                                  className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
                                  title="Delete photo"
                                >
                                  <FaTimes size={14} />
                                </button>
                              </div>
                            ))}

                          </div>
                        </div>
                      )}

                      {galleryPreviews.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-text-secondary mb-3">
                            New Photos to Upload ({galleryPreviews.length})
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                            {galleryPreviews.map((preview, index) => (
                              <div key={index} className="aspect-square overflow-hidden rounded border-2 border-dashed border-rose-400 bg-[rgba(255,46,136,0.1)]">
                                <img
                                  src={preview}
                                  alt={`New ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                          <div className="text-center">
                            <button
                              onClick={handleGalleryUpload}
                              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                            >
                              Upload {galleryPreviews.length} Photo{galleryPreviews.length > 1 ? "s" : ""}
                            </button>
                            <button
                              onClick={() => {
                                setGalleryFiles([]);
                                setGalleryPreviews([]);
                                document.getElementById("galleryUpload").value = "";
                              }}
                              className="ml-3 px-6 py-3 bg-[rgba(255,255,255,0.03)]0 text-white rounded-lg hover:bg-gray-600 transition"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {galleryPreviews.length === 0 && (
                        <div className="text-center py-8">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleGalleryChange}
                            className="hidden"
                            id="galleryUpload"
                          />
                          <label
                            htmlFor="galleryUpload"
                            className="cursor-pointer inline-flex items-center px-6 py-4 bg-primary-pink shadow-[0_0_15px_rgba(255,46,136,0.4)] text-white text-lg rounded-lg hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)] transition shadow-lg"
                          >
                            <FaUpload className="mr-3 text-xl" />
                            Add More Photos to Gallery
                          </label>
                          {profile.photos.length === 0 && (
                            <p className="mt-4 text-text-muted">
                              Add photos to make your profile more attractive!
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {isConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary rounded-lg p-6 w-80">
            <h3 className="text-lg font-bold text-text-primary drop-shadow-sm mb-3">
              Delete Photo?
            </h3>
            <p className="text-text-secondary mb-5">
              Are you sure you want to delete this photo?
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsConfirmOpen(false);
                  setConfirmDeleteIndex(null);
                }}
                className="px-4 py-2 bg-[rgba(255,255,255,0.1)] text-white rounded hover:bg-[rgba(255,255,255,0.2)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGalleryPhoto}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {viewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          onClick={() => setViewImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={viewImage}
              alt="Full View"
              className="max-h-[90vh] rounded-lg shadow-xl"
            />
            <button
              onClick={() => setViewImage(null)}
              className="absolute top-3 right-3 bg-black bg-opacity-60 text-white p-2 rounded-full hover:bg-opacity-80"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}

    </div>

  );
};


const InfoCard = ({ title, icon, items }) => (
  <div className="bg-glass-card backdrop-blur-xl shadow-glass text-text-primary border border-glass-border rounded-lg p-5 h-full flex flex-col">
    <div className="flex items-center mb-5 border-b border-glass-border/50 pb-3">
      <div className="p-2.5 bg-[rgba(255,46,136,0.1)] rounded-lg mr-3 flex-shrink-0 shadow-sm border border-[rgba(255,46,136,0.15)]">{icon}</div>
      <h3 className="font-bold text-text-primary drop-shadow-sm leading-snug flex-1 min-w-0 break-words">{title}</h3>
    </div>
    <div className="space-y-4 flex-1">
      {items.map((item, index) => (
        <div key={index} className="flex flex-col">
          <span className="text-xs text-text-secondary uppercase tracking-wider mb-1">{item.label}</span>
          <span className="text-sm font-semibold text-text-primary drop-shadow-sm">
            {item.value || "-"}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const SectionCard = ({ title, icon, items, className = "", style = {} }) => (
  <div 
    className={`bg-glass-card backdrop-blur-xl shadow-glass text-text-primary border border-glass-border rounded-lg p-6 ${className}`}
    style={style}
  >
    <div className="flex items-center mb-5 border-b border-glass-border/50 pb-4">
      <div className="mr-3 text-primary-pink text-xl">
        {icon}
      </div>
      <h3 className="text-lg font-bold text-text-primary drop-shadow-sm">{title}</h3>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((item, index) => (
        <div
          key={index}
          className="relative flex flex-col"
          style={{ zIndex: 100 - index }}
        >
          <div className="text-xs uppercase tracking-wider font-semibold text-text-secondary mb-1">
            {item.label}
          </div>
          <div className="text-sm text-text-primary drop-shadow-sm font-medium break-words">
            {typeof item.value === "string" || typeof item.value === "number"
              ? item.value || "Not specified"
              : item.value}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default Profile;